// ============================================================================
// Webhook Signature Verifier — HMAC helpers + timing-safe compare
//
// Providers sign webhook payloads with HMAC so we can verify the request came
// from them (not a spoofed source). Each provider uses a different shape:
//   - Facebook: HMAC-SHA1 (legacy)  header `x-hub-signature`       = `sha1=<hex>`
//   - Facebook: HMAC-SHA256         header `x-hub-signature-256`   = `sha256=<hex>`
//   - Line:     HMAC-SHA256         header `x-line-signature`      = `<base64>`
//   - Shopee:   HMAC-SHA256         header `authorization`          = `<hex>` (prefixed)
//
// All helpers here accept the RAW REQUEST BODY (bytes as the provider sent them).
// Re-serializing JSON is NEVER safe — key ordering + whitespace differences
// will break the HMAC compare. Routes must read the body via `arrayBuffer()`
// BEFORE any JSON parser touches it.
//
// Timing-safe compare: `crypto.subtle.timingSafeEqual` does NOT exist in the
// Workers runtime (Node-only). We implement constant-time XOR in userland —
// MUST be used whenever comparing an attacker-controllable HMAC candidate to
// the computed one, or an attacker can recover the secret byte-by-byte via
// differential timing.
// ============================================================================

export type HmacAlgorithm = 'SHA-1' | 'SHA-256' | 'SHA-512'

/**
 * Compute HMAC(key, data) and return the raw bytes. Callers choose their own
 * encoding (hex / base64) — do NOT pre-encode here or the constant-time
 * comparison below can end up comparing different-length strings.
 */
export async function hmac(
  secret: string,
  data: ArrayBuffer | Uint8Array,
  algorithm: HmacAlgorithm,
): Promise<Uint8Array> {
  assertNonEmptySecret(secret)
  const keyBytes = new TextEncoder().encode(secret)
  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'HMAC', hash: algorithm },
    false,
    ['sign'],
  )
  const dataBytes = (data instanceof Uint8Array ? data : new Uint8Array(data)) as Uint8Array<ArrayBuffer>
  const sig = await crypto.subtle.sign('HMAC', key, dataBytes)
  return new Uint8Array(sig)
}

/** Convenience wrapper: HMAC then hex-encode (lowercase). */
export async function hmacHex(
  secret: string,
  data: ArrayBuffer | Uint8Array,
  algorithm: HmacAlgorithm,
): Promise<string> {
  return bytesToHex(await hmac(secret, data, algorithm))
}

/** Convenience wrapper: HMAC then base64-encode (standard, no url-safe). */
export async function hmacBase64(
  secret: string,
  data: ArrayBuffer | Uint8Array,
  algorithm: HmacAlgorithm,
): Promise<string> {
  return bytesToBase64(await hmac(secret, data, algorithm))
}

// ============================================================================
// Timing-safe compare
// ============================================================================

/**
 * Constant-time byte comparison. Returns true iff both inputs have identical
 * length AND identical contents. The execution time depends only on the
 * length of `a` (not on how many bytes match) — this prevents attackers
 * from learning the HMAC byte-by-byte via response timing.
 */
export function timingSafeEqualBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) {
    diff |= a[i] ^ b[i]
  }
  return diff === 0
}

/**
 * Constant-time string comparison. Decodes UTF-8 bytes before comparing, so
 * two semantically equal strings with identical byte representations match.
 * Do NOT use for comparing arbitrary untrusted bytes — use the bytes variant
 * instead (a malformed UTF-8 pair would fall back to mismatch without telling
 * you why).
 */
export function timingSafeEqualString(a: string, b: string): boolean {
  const encoder = new TextEncoder()
  return timingSafeEqualBytes(encoder.encode(a), encoder.encode(b))
}

// ============================================================================
// Header helpers — extract the signature from a provider-specific header shape
// ============================================================================

/**
 * `sha1=abc...` or `sha256=abc...` → just the hex part. Returns null if the
 * prefix doesn't match the expected algorithm or the header is absent/blank.
 */
export function parsePrefixedSignature(
  headerValue: string | null | undefined,
  prefix: 'sha1' | 'sha256' | 'sha512',
): string | null {
  if (!headerValue) return null
  const expected = `${prefix}=`
  if (!headerValue.toLowerCase().startsWith(expected)) return null
  const value = headerValue.slice(expected.length).trim()
  return value.length > 0 ? value : null
}

// ============================================================================
// Payload hashing — used for audit log without keeping PII
// ============================================================================

/** SHA-256 hex digest of the raw request body. Always 64 chars. */
export async function sha256Hex(data: ArrayBuffer | Uint8Array): Promise<string> {
  const bytes = (data instanceof Uint8Array ? data : new Uint8Array(data)) as Uint8Array<ArrayBuffer>
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return bytesToHex(new Uint8Array(digest))
}

// ============================================================================
// Encoding helpers
// ============================================================================

/** Lowercase hex. */
export function bytesToHex(bytes: Uint8Array): string {
  let out = ''
  for (const b of bytes) {
    out += b.toString(16).padStart(2, '0')
  }
  return out
}

/** Hex string → Uint8Array. Throws if the hex is malformed. */
export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new VerifierError('INVALID_FORMAT', 'hex string has odd length')
  }
  // Strict alphabet check UP FRONT. `parseInt(..., 16)` is permissive —
  // e.g. `parseInt('+a', 16) === 10` because it strips a sign, and
  // `parseInt('ax', 16) === 10` because it stops at the first invalid
  // char. Both would silently accept crafted inputs and produce unexpected
  // bytes. Regex first → every byte goes through the parser cleanly.
  if (!/^[0-9a-fA-F]*$/.test(hex)) {
    throw new VerifierError('INVALID_FORMAT', 'hex string contains non-hex characters')
  }
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    out[i / 2] = parseInt(hex.substring(i, i + 2), 16)
  }
  return out
}

/** Standard base64 (no url-safe remap). */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary)
}

/** Base64 → bytes. Throws on malformed input. */
export function base64ToBytes(b64: string): Uint8Array {
  try {
    const binary = atob(b64)
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
    return out
  } catch {
    throw new VerifierError('INVALID_FORMAT', 'invalid base64')
  }
}

// ============================================================================
// High-level verifiers — match a candidate against the expected HMAC safely
// ============================================================================

/** Verify a hex-encoded HMAC candidate against secret + data in constant time. */
export async function verifyHmacHex(
  candidateHex: string,
  secret: string,
  data: ArrayBuffer | Uint8Array,
  algorithm: HmacAlgorithm,
): Promise<boolean> {
  let candidate: Uint8Array
  try {
    candidate = hexToBytes(candidateHex.trim())
  } catch {
    return false
  }
  const expected = await hmac(secret, data, algorithm)
  return timingSafeEqualBytes(candidate, expected)
}

/** Verify a base64-encoded HMAC candidate against secret + data in constant time. */
export async function verifyHmacBase64(
  candidateB64: string,
  secret: string,
  data: ArrayBuffer | Uint8Array,
  algorithm: HmacAlgorithm,
): Promise<boolean> {
  let candidate: Uint8Array
  try {
    candidate = base64ToBytes(candidateB64.trim())
  } catch {
    return false
  }
  const expected = await hmac(secret, data, algorithm)
  return timingSafeEqualBytes(candidate, expected)
}

// ============================================================================
// Assertions
// ============================================================================

function assertNonEmptySecret(secret: string): void {
  if (!secret || typeof secret !== 'string') {
    throw new VerifierError('INVALID_INPUT', 'secret must be a non-empty string')
  }
}

// ============================================================================
// Errors
// ============================================================================

export type VerifierErrorCode = 'INVALID_INPUT' | 'INVALID_FORMAT'

export class VerifierError extends Error {
  constructor(public readonly code: VerifierErrorCode, message: string) {
    super(message)
    this.name = 'VerifierError'
  }
}
